'use client';

import { useEffect, useRef, useState } from 'react';
import styles from './expandableText.module.scss';
import { Button } from '@goodneighbor/ui';
import { ChevronDown } from '@goodneighbor/ui/icons/untitled';
import { cva, VariantProps } from 'class-variance-authority';

const expandableTextClass = cva(styles.text, {
  variants: {
    expanded: {
      true: styles.expanded
    },
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
      xl: styles.xl
    }
  },
  defaultVariants: {
    size: 'sm'
  }
});

type ExpandableTextProps = VariantProps<typeof expandableTextClass> & {
  children: React.ReactNode;
  expandLinkText?: string;
  collapseLinkText?: string;
};

export default function ExpandableText({
  children,
  expandLinkText = 'Read more',
  collapseLinkText = 'Read less',
  size
}: ExpandableTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [showExpandButton, setShowExpandButton] = useState(true);
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    const text = textRef.current;
    if (text) {
      const isOverflowing = text.scrollHeight > text.clientHeight;
      setShowExpandButton(isOverflowing);
      setShowFullDescription(!isOverflowing);
    }
  }, [children]);

  return (
    <div>
      <div
        ref={textRef}
        className={expandableTextClass({ expanded: showFullDescription, size })}
      >
        {children}
      </div>
      {showExpandButton && (
        <Button
          type="button"
          color="link-color"
          size="xs"
          className={styles.readMoreButton}
          onClick={() => setShowFullDescription((prev) => !prev)}
          aria-expanded={showFullDescription}
          data-expanded={showFullDescription}
          iconTrailing={
            <ChevronDown
              className={styles.chevron}
              aria-hidden="true"
              focusable="false"
            />
          }
        >
          {showFullDescription ? collapseLinkText : expandLinkText}
        </Button>
      )}
    </div>
  );
}
